import IconifyIcon from '@/components/IconifyIcon/IconifyIcon';
import { Pagination } from '@/components/shared/Pagination';
import { SearchInput } from '@/components/SearchInput';
import ShadcnUITable, { ColumnsProps } from '@/components/Table/ShadcnUITable';
import { TableCell, TableRow } from '@/components/ui/table';
import { CTA_ACTION_TYPE, CTA_TYPE, MAGNET_TYPE } from '@/constants/bot';
import { ILinkForCTA, ILinksForCTAResponse } from '@/types/bots';
import { IBaseQueryParams } from '@/types/common';
import { ICTAOption } from '@/types/page-cta';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import MagnetActionType from './MagnetActionType';
import { Badge } from '@/components/ui/badge';

interface IAllURLsProps {
  allURLData?: ILinksForCTAResponse;
  isLoading?: boolean;
  queryParams: IBaseQueryParams;
  setQueryParams: (params: IBaseQueryParams) => void;
}

const CustomChip = (props: { value: any }) => {
  const label = {
    [CTA_TYPE.LEAD_MAGNET]: 'Lead Magnet',
    [CTA_TYPE.ACTION]: 'Action',
    [CTA_ACTION_TYPE.CONNECT_TO_AGENT]: 'Connect to Agent',
    [CTA_ACTION_TYPE.OPEN_CHATBOT]: 'Open Chatbot',
    [CTA_ACTION_TYPE.SHOW_CALENDAR]: 'Show Calendar',
    'N/A': 'Not Available',
  };

  // Map value to Badge variant and className
  const getVariantAndClassName = (value: string) => {
    switch (value?.toLowerCase()) {
      case CTA_TYPE.LEAD_MAGNET:
        return { variant: 'info' as const, className: 'bg-blue-50' };
      case CTA_TYPE.ACTION:
        return { variant: 'error' as const, className: 'bg-red-100' };
      case CTA_ACTION_TYPE.CONNECT_TO_AGENT:
        return { variant: 'success' as const, className: 'bg-green-50' };
      case CTA_ACTION_TYPE.OPEN_CHATBOT:
        return { variant: 'error' as const, className: 'bg-red-50' };
      case CTA_ACTION_TYPE.SHOW_CALENDAR:
        return { variant: 'warning' as const, className: 'bg-[#FFF0E8]' };
      default:
        return { variant: 'neutral' as const, className: 'bg-gray-200' };
    }
  };

  const { variant, className } = getVariantAndClassName(props.value);

  return (
    <Badge variant={variant} size="sm" className={clsx('rounded-3xl !font-medium text-base w-fit', className)}>
      {label[props.value as keyof typeof label] || props.value || 'Not Available'}
    </Badge>
  );
};

const AllURLs: React.FunctionComponent<IAllURLsProps> = ({
  allURLData,
  isLoading,
  queryParams,
  setQueryParams,
}) => {
  const [collapsedRows, setCollapsedRows] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');

  const { data: links, totalItems, totalPages } = allURLData || {};

  const handleToggleCollapsed = (id: number) => {
    setCollapsedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleSearchLink = React.useCallback(
    debounce((searchText: string) => {
      setQueryParams({
        page: 1,
        searchText,
      });
    }, 500),
    [setQueryParams],
  );

  const columns: ColumnsProps[] = [
    {
      title: 'All URLs',
      key: 'link',
      styles: 'w-3/5',
    },
    {
      title: 'Date added on',
      key: 'dateAdded',
      styles: 'w-1/5',
      renderNode: (row: ILinkForCTA) => (
        <p className='text-s text-black-200 font-medium'>
          {dayjs(row.dateAdded).format('DD MMM, YYYY')}
        </p>
      ),
    },
    {
      title: 'More',
      key: 'expand',
      styles: 'w-1/5',
      renderNode: (row: ILinkForCTA) => (
        <span
          className='text-primary-500 cursor-pointer'
          onClick={() => handleToggleCollapsed(row.link_id)}
        >
          {collapsedRows.includes(row.link_id) ? (
            <IconifyIcon name='ic:outline-minus' size={18} color='#666666' />
          ) : (
            <IconifyIcon name='ic:outline-plus' size={18} color='#666666' />
          )}
        </span>
      ),
    },
  ];

  const customRenderRow = (row: ILinkForCTA, rowIndex: number) => {
    const rows: ICTAOption[] = Object.keys(row?.options || {})
      .filter((optionKey) => row.options[optionKey as keyof ILinkForCTA['options']] !== null)
      .map((optionKey) => ({
        ...(row.options[optionKey as keyof ILinkForCTA['options']] || {}),
        cta: optionKey
          .split('_')
          .map((it) => it.charAt(0).toUpperCase() + it.slice(1))
          .join(' '),
      }));

    const collapsedColumns: ColumnsProps[] = [
      {
        title: 'CTA',
        key: 'cta',
        styles: 'w-1/4 border-r',
      },
      {
        title: 'Type',
        key: 'type',
        styles: 'w-1/4 border-r',
        renderNode: (row: ICTAOption) => <CustomChip value={row.type} />,
      },
      {
        title: 'Action Type',
        key: 'actionType',
        styles: 'w-1/4 border-r',
        renderNode: (row: ICTAOption) => {
          if (!row?.type) {
            return <CustomChip value='N/A' />; // Handle case where type is missing (e.g., null options)
          }
          if (row.type === CTA_TYPE.LEAD_MAGNET) {
            return <MagnetActionType fileType={row.config?.magnet_type as MAGNET_TYPE} />;
          }
          return <CustomChip value={row.config?.action_type || 'N/A'} />;
        },
      },
      {
        title: 'CTA Label',
        key: 'label',
        styles: 'w-1/4',
      },
    ];

    return (
      <React.Fragment key={row.link_id}>
        <TableRow
          className={clsx(
            'hover:bg-[#F9FAFA] transition-colors duration-150',
            collapsedRows.includes(row.link_id) ? 'bg-[#F9FAFA]' : '',
          )}
        >
          {columns.map((column, index) => (
            <TableCell key={index} className='font-medium text-gray-600'>
              {column?.renderNode
                ? column.renderNode(row, rowIndex)
                : typeof row[column.key as keyof ILinkForCTA] === 'object'
                  ? '-'
                  : String(row[column.key as keyof ILinkForCTA])}
            </TableCell>
          ))}
        </TableRow>
        {collapsedRows.includes(row.link_id) && (
          <TableRow className='border-0'>
            <TableCell className='bg-white p-0' colSpan={9999}>
              <ShadcnUITable rows={rows || []} columns={collapsedColumns} />
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    handleSearchLink(e.target.value);
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-end">
        <SearchInput
          className="w-[200px] shrink-0"
          value={searchText}
          onChange={handleSearchChange}
          placeholder="Search pages…"
        />
      </div>
      <div className="flex flex-col gap-4">
        <ShadcnUITable
          className="border border-gray-200 rounded-lg"
          rows={links || []}
          columns={columns}
          customRenderRow={customRenderRow}
          noFoundMsg="No URLs found"
          skeletonLoading={isLoading}
        />
        <Pagination
          currentPage={Number(queryParams.page ?? 1)}
          totalPages={Number(totalPages ?? 1)}
          onPageChange={(page) => setQueryParams({ ...queryParams, page })}
        />
      </div>
    </div>
  );
};

export default AllURLs;
